import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FormControlLabel, Switch } from '@mui/material';
import AutocompleteCDS, {
  onAutocompleteChangeInForm,
} from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import { getFloodProductOverrideOptions } from 'app/shared/components/loanManagement/loanActionsGroup/LoanActionFloodServiceRow.utils';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';

import {
  loadServicelinkFloodProductsAction,
  selectFloodProductOptions,
} from 'store/loanActions/servicelinkFloodProducts';

import {
  COMMENTS_FIELD_NAME,
  COMMENTS_MAX_LENGTH,
  COMMENTS_ROWS,
  INITIAL_FORM_STATE,
} from './FloodOrderOptionsForm.constants';
import { schema } from './FloodOrderOptionsForm.schema';
import { FloodOrderOptionsFormTestIds } from './FloodOrderOptionsForm.testIds';

const FloodOrderOptionsForm = ({ isLoading, setIsLoading, formRef, onSubmit }) => {
  const dispatch = useDispatch();
  const floodProductOptions = useSelector(selectFloodProductOptions);

  const { form, setSchema, setInForm, validateForm, validateField, handleChange } =
    useForm(INITIAL_FORM_STATE);

  const productOptions = useMemo(
    () => getFloodProductOverrideOptions(floodProductOptions),
    [floodProductOptions],
  );

  const onSubmitForm = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm({ noFormChangedValidate: true }))) {
        return;
      }

      setIsLoading(true);

      onSubmit({
        productKey: form.product?.value ?? null,
        rush: !!form.rush,
        comments: form.comments?.trim() || null,
      }).finally(() => setIsLoading(false));
    },
    [validateForm, form, onSubmit, setIsLoading],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onToggleRush = useCallback(e => setInForm('rush', e.target.checked), [setInForm]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  useEffectSafeStrictMode(() => {
    dispatch(loadServicelinkFloodProductsAction());
  }, [dispatch]);

  return (
    <ModalFormWrapper
      isFormChanged
      formRef={formRef}
      formTestId="flood-order-options-form"
      isSubmitting={isLoading}
      submitButtonName="Submit"
      onSubmit={onSubmitForm}
    >
      <AutocompleteCDS
        disableClearable
        containerClassName="mb-24"
        dataTestId={FloodOrderOptionsFormTestIds.PRODUCT_SELECT}
        disabled={isLoading}
        label="Product"
        name="product"
        options={productOptions}
        value={form.product || ''}
        onChange={onAutocompleteChange}
      />
      <FormControlLabel
        control={<Switch checked={!!form.rush} disabled={isLoading} onChange={onToggleRush} />}
        data-testid={FloodOrderOptionsFormTestIds.RUSH_SWITCH}
        label="Rush (high-priority) order"
      />
      <TextFieldCDS
        multiline
        dataTestId={FloodOrderOptionsFormTestIds.COMMENTS_INPUT}
        disabled={isLoading}
        label="Comments"
        maxLength={COMMENTS_MAX_LENGTH}
        name={COMMENTS_FIELD_NAME}
        rows={COMMENTS_ROWS}
        value={form.comments ?? ''}
        onChange={handleChange}
      />
    </ModalFormWrapper>
  );
};

FloodOrderOptionsForm.propTypes = {
  ...WithLoadingPropTypes,
  onSubmit: PropTypes.func.isRequired,
};

export default compose(withLoading, memo)(FloodOrderOptionsForm);
